/**
 * TcmCommentModal — comment-capture modal for continuous-watch drift accept /
 * reject.
 *
 * Used by TcmDriftTable's row action footer (accept, reject), the bulk
 * accept bar, and the drift page's expanded rows. Built on the Carbon modal
 * (components/carbon/Modal): headless UI focus trap, Escape to close (held
 * back while a request is in flight), a pinned header, the note field and
 * Carbon's footer button set.
 *
 * The comment is optional (the backend accepts {comment?: string}). We leave
 * the textarea unvalidated so an admin can ship an accept with no note —
 * they're committing the verdict by clicking Confirm. Two variants:
 * `primary` (accept) and `danger` (reject), which takes Coral as a Carbon
 * danger modal.
 *
 * Voice: titles + button labels are owned by the caller so the same modal
 * can serve "Accept baseline" and "Reject drift" without copy duplication.
 * The placeholder text inside the textarea is the only string the modal
 * owns directly — defaults to an advisor-voice ask if the caller doesn't set
 * it.
 */

import { useEffect, useRef, useState } from 'react'
import { Modal } from '@/components/carbon/Modal'
import { TextArea } from '@/components/carbon/TextArea'

export interface TcmCommentModalProps {
  isOpen: boolean
  title: string
  // Tone of the confirm button. `primary` is the Soft Green accept button.
  // `danger` is the Coral reject button. Default is primary.
  confirmVariant?: 'primary' | 'danger'
  confirmLabel?: string
  // Optional override for the textarea placeholder. Defaults to a
  // conversational ask matching the trusted-advisor voice.
  placeholder?: string
  // Caller-owned. We hand the trimmed comment up; empty string means the
  // admin chose to leave a comment off.
  onConfirm: (comment: string) => void | Promise<void>
  onCancel: () => void
  isSubmitting?: boolean
}

export default function TcmCommentModal({
  isOpen,
  title,
  confirmVariant = 'primary',
  confirmLabel,
  placeholder,
  onConfirm,
  onCancel,
  isSubmitting = false,
}: TcmCommentModalProps) {
  const [comment, setComment] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Reset the textarea every time the modal opens. Without this, a user
  // who opened reject after an accept would see the accept's comment
  // pre-populated — confusing at best, audit-poisoning at worst.
  useEffect(() => {
    if (isOpen) {
      setComment('')
    }
  }, [isOpen])

  const resolvedConfirmLabel =
    confirmLabel ?? (confirmVariant === 'danger' ? 'Reject' : 'Confirm')

  const resolvedPlaceholder =
    placeholder ?? 'Say a few words about why. Optional, but it helps the next admin understand the call.'

  // Submit is allowed even with no comment — the backend signature treats
  // it as optional. We trim trailing whitespace so the audit log doesn't
  // store accidentally-padded text.
  const handleSubmit = () => {
    if (isSubmitting) return
    void onConfirm(comment.trim())
  }

  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      title={title}
      size="sm"
      danger={confirmVariant === 'danger'}
      initialFocus={textareaRef}
      primaryAction={{
        label: isSubmitting ? 'Working' : resolvedConfirmLabel,
        onClick: handleSubmit,
        loading: isSubmitting,
      }}
      secondaryAction={{ label: 'Cancel', onClick: onCancel, disabled: isSubmitting }}
    >
      <TextArea
        id="tcm-comment-modal-textarea"
        ref={textareaRef}
        label="Note for the audit log"
        helper="Stays with the drift for good, so the next admin can follow the decision."
        rows={5}
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder={resolvedPlaceholder}
        disabled={isSubmitting}
      />
    </Modal>
  )
}
